const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const getHeaders = () => {
  const token = localStorage.getItem('ems_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

const handleResponse = async (response) => {
  const contentType = response.headers.get('content-type') || '';

  // Read body only once to avoid "body stream already read" errors
  let rawText = '';
  try {
    rawText = await response.text();
  } catch (e) {
    rawText = '';
  }

  const maybeJson = contentType.includes('application/json');
  const parsed = maybeJson && rawText ? (() => {
    try {
      return JSON.parse(rawText);
    } catch (e) {
      return null;
    }
  })() : null;

  if (!response.ok) {
    if (response.status === 401) {
      // Only redirect to login if we're on a protected page (not already on login)
      // Don't clear tokens on auth-related endpoints (login, register, etc.)
      const isAuthEndpoint = response.url.includes('/auth/login') || response.url.includes('/auth/register');
      if (!isAuthEndpoint && window.location.pathname !== '/login') {
        localStorage.removeItem('ems_token');
        localStorage.removeItem('ems_user');
        sessionStorage.removeItem('ems_token');
        sessionStorage.removeItem('ems_user');
        window.location.href = '/login';
      }
    }
    const detail = (parsed && (parsed.detail || parsed.message)) || 'Request failed';
    throw new Error(detail);
  }

  if (maybeJson) return parsed;
  return rawText;
};

// Auth API
export const authApi = {
  login: (email, password) => 
    fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify({ email, password })
    }).then(handleResponse),
    
  // Registration is disabled (private application)
  register: () => Promise.reject(new Error('Registration is disabled')),

  me: () =>
    fetch(`${API_URL}/auth/me`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),
    
  logout: () =>
    fetch(`${API_URL}/auth/logout`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  changePassword: (currentPassword, newPassword) =>
    fetch(`${API_URL}/auth/change-password`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword })
    }).then(handleResponse),

  forgotPassword: (email, turnstileToken) =>
    fetch(`${API_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify({ email, turnstile_token: turnstileToken || null })
    }).then(handleResponse),

  verifyResetToken: (token) =>
    fetch(`${API_URL}/auth/verify-reset-token?token=${encodeURIComponent(token)}`, {
      credentials: 'omit'
    }).then(handleResponse),

  resetPassword: (token, newPassword, turnstileToken) =>
    fetch(`${API_URL}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify({ token, new_password: newPassword, turnstile_token: turnstileToken || null })
    }).then(handleResponse)
};

// Users API
export const usersApi = {
  getAll: () =>
    fetch(`${API_URL}/users`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  create: (data) =>
    fetch(`${API_URL}/users`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),

  getParticipants: () =>
    fetch(`${API_URL}/users/participants`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),
    
  getOne: (userId) =>
    fetch(`${API_URL}/users/${userId}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),
    
  update: (userId, data) =>
    fetch(`${API_URL}/users/${userId}`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),
    
  delete: (userId) =>
    fetch(`${API_URL}/users/${userId}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),
    
  bulkImport: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return fetch(`${API_URL}/users/bulk-import`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('ems_token')}` },
      credentials: 'omit',
      body: formData
    }).then(handleResponse);
  }
};

// Exercises API
export const exercisesApi = {
  getAll: (status, clientId, subCompanyId) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (clientId) params.append('client_id', clientId);
    if (subCompanyId) params.append('sub_company_id', subCompanyId);
    const qs = params.toString();

    return fetch(`${API_URL}/exercises${qs ? `?${qs}` : ''}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse);
  },
    
  getOne: (exerciseId) =>
    fetch(`${API_URL}/exercises/${exerciseId}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),
    
  create: (data) =>
    fetch(`${API_URL}/exercises`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),
    
  update: (exerciseId, data) =>
    fetch(`${API_URL}/exercises/${exerciseId}`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),
    
  delete: (exerciseId) =>
    fetch(`${API_URL}/exercises/${exerciseId}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),
    
  control: (exerciseId, action) =>
    fetch(`${API_URL}/exercises/${exerciseId}/control?action=${action}`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  duplicate: (exerciseId) =>
    fetch(`${API_URL}/exercises/${exerciseId}/duplicate`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  reset: (exerciseId) =>
    fetch(`${API_URL}/exercises/${exerciseId}/reset`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse)
};

// Injects API
export const injectsApi = {
  getAll: (exerciseId) =>
    fetch(`${API_URL}/injects${exerciseId ? `?exercise_id=${exerciseId}` : ''}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  getOne: (injectId) =>
    fetch(`${API_URL}/injects/${injectId}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  create: (data) =>
    fetch(`${API_URL}/injects`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),

  update: (injectId, data) =>
    fetch(`${API_URL}/injects/${injectId}`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),

  delete: (injectId) =>
    fetch(`${API_URL}/injects/${injectId}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  trigger: (injectId) =>
    fetch(`${API_URL}/injects/${injectId}/trigger`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  complete: (injectId) =>
    fetch(`${API_URL}/injects/${injectId}/complete`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  bulkImport: (exerciseId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return fetch(`${API_URL}/injects/bulk-import?exercise_id=${exerciseId}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('ems_token')}`
      },
      credentials: 'omit',
      body: formData
    }).then(handleResponse);
  }
};

// Objectives API
export const objectivesApi = {
  getAll: (exerciseId) =>
    fetch(`${API_URL}/objectives${exerciseId ? `?exercise_id=${encodeURIComponent(exerciseId)}` : ''}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  getOne: (objectiveId) =>
    fetch(`${API_URL}/objectives/${objectiveId}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  create: (data) =>
    fetch(`${API_URL}/objectives`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),

  update: (objectiveId, data) =>
    fetch(`${API_URL}/objectives/${objectiveId}`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),

  delete: (objectiveId) =>
    fetch(`${API_URL}/objectives/${objectiveId}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  // V14.9 — KPIs + section-tagged notes
  listKpis: (objectiveId) =>
    fetch(`${API_URL}/objectives/${objectiveId}/kpis`, {
      headers: getHeaders(), credentials: 'omit',
    }).then(handleResponse),

  createKpi: (objectiveId, data) =>
    fetch(`${API_URL}/objectives/${objectiveId}/kpis`, {
      method: 'POST', headers: getHeaders(), credentials: 'omit',
      body: JSON.stringify({ objective_id: objectiveId, ...data }),
    }).then(handleResponse),

  deleteKpi: (kpiId) =>
    fetch(`${API_URL}/objectives/kpis/${kpiId}`, {
      method: 'DELETE', headers: getHeaders(), credentials: 'omit',
    }).then(handleResponse),

  importKpis: (exerciseId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    const token = localStorage.getItem('ems_token');
    return fetch(`${API_URL}/objectives/${exerciseId}/import-kpis`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      credentials: 'omit',
      body: fd,
    }).then(handleResponse);
  },

  listNotes: (objectiveId, kpiId) => {
    const qs = kpiId ? `?kpi_id=${encodeURIComponent(kpiId)}` : '';
    return fetch(`${API_URL}/objectives/${objectiveId}/notes${qs}`, {
      headers: getHeaders(), credentials: 'omit',
    }).then(handleResponse);
  },

  // V14.10.7 — lightweight per-KPI / per-eval note count aggregator
  noteCounts: (objectiveId) =>
    fetch(`${API_URL}/objectives/${objectiveId}/notes/counts`, {
      headers: getHeaders(), credentials: 'omit',
    }).then(handleResponse),

  createNote: (data) =>
    fetch(`${API_URL}/objectives/notes`, {
      method: 'POST', headers: getHeaders(), credentials: 'omit',
      body: JSON.stringify(data),
    }).then(handleResponse),

  listSections: () =>
    fetch(`${API_URL}/objectives/notes/sections`, {
      headers: getHeaders(), credentials: 'omit',
    }).then(handleResponse),
};

// Evaluations API
export const evaluationsApi = {
  getAll: (exerciseId) =>
    fetch(`${API_URL}/evaluations${exerciseId ? `?exercise_id=${encodeURIComponent(exerciseId)}` : ''}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  getOne: (evaluationId) =>
    fetch(`${API_URL}/evaluations/${evaluationId}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  getMetadata: () =>
    fetch(`${API_URL}/evaluations/metadata`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  create: (data) =>
    fetch(`${API_URL}/evaluations`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),

  update: (evaluationId, data) =>
    fetch(`${API_URL}/evaluations/${evaluationId}`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),

  delete: (evaluationId) =>
    fetch(`${API_URL}/evaluations/${evaluationId}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),
};

export const injectLibraryApi = {
  importFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return fetch(`${API_URL}/inject-library/import`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('ems_token')}`
      },
      credentials: 'omit',
      body: formData
    }).then(handleResponse);
  },

  list: ({ category, company, search, limit }) => {
    const params = new URLSearchParams();
    if (category && category !== 'all') params.set('category', category);
    if (company && company !== 'all') params.set('company', company);
    if (search) params.set('q', search);
    if (limit) params.set('limit', limit);
    const qs = params.toString() ? `?${params.toString()}` : '';

    return fetch(`${API_URL}/inject-library${qs}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse);
  },

  listCompanies: () =>
    fetch(`${API_URL}/inject-library/companies`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  listCategories: () =>
    fetch(`${API_URL}/inject-library/categories`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  addToExercise: (exerciseId, templateId) =>
    fetch(`${API_URL}/inject-library/add-to-exercise?exercise_id=${encodeURIComponent(exerciseId)}&template_id=${encodeURIComponent(templateId)}`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),
};

// Clients / Sub-companies API
export const clientsApi = {
  getAll: () =>
    fetch(`${API_URL}/clients`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  create: (data) =>
    fetch(`${API_URL}/clients`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),

  update: (clientId, data) =>
    fetch(`${API_URL}/clients/${clientId}`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),

  delete: (clientId) =>
    fetch(`${API_URL}/clients/${clientId}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),
};

export const subCompaniesApi = {
  getAll: (clientId) => {
    const qs = clientId ? `?client_id=${encodeURIComponent(clientId)}` : '';
    return fetch(`${API_URL}/sub-companies${qs}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse);
  },

  create: (data) =>
    fetch(`${API_URL}/sub-companies`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),

  update: (subCompanyId, data) =>
    fetch(`${API_URL}/sub-companies/${subCompanyId}`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),

  delete: (subCompanyId) =>
    fetch(`${API_URL}/sub-companies/${subCompanyId}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),
};

 

// Responses API
export const uploadsApi = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return fetch(`${API_URL}/uploads`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('ems_token')}`
      },
      credentials: 'omit',
      body: formData
    }).then(handleResponse);
  }
};

export const responsesApi = {
  getAll: ({ exerciseId, injectId, objectiveId, participantId }) => {
    const params = new URLSearchParams();
    if (exerciseId) params.set('exercise_id', exerciseId);
    if (injectId) params.set('inject_id', injectId);
    if (objectiveId) params.set('objective_id', objectiveId);
    if (participantId) params.set('participant_id', participantId);

    const qs = params.toString() ? `?${params.toString()}` : '';
    return fetch(`${API_URL}/responses${qs}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse);
  },

  create: (data) =>
    fetch(`${API_URL}/responses`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),
};

// Timeline API
export const timelineApi = {
  get: (exerciseId) =>
    fetch(`${API_URL}/timeline/${exerciseId}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),
    
  addNote: (exerciseId, note, injectId) => {
    const params = new URLSearchParams();
    params.append('note', note);
    if (injectId) params.append('inject_id', injectId);

    return fetch(`${API_URL}/timeline/${exerciseId}/note?${params.toString()}`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse);
  }
};

// Time Tracking API
export const timeTrackingApi = {
  getEntries: (userId, exerciseId, category) => {
    const params = new URLSearchParams();
    if (userId) params.append('user_id', userId);
    if (exerciseId) params.append('exercise_id', exerciseId);
    if (category) params.append('category', category);
    return fetch(`${API_URL}/time-entries?${params}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse);
  },
  
  create: (data) =>
    fetch(`${API_URL}/time-entries`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),
    
  getSummary: (userId, exerciseId) => {
    const params = new URLSearchParams();
    if (userId) params.append('user_id', userId);
    if (exerciseId) params.append('exercise_id', exerciseId);
    return fetch(`${API_URL}/time-entries/summary?${params}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse);
  }
};

// Notifications API
export const notificationsApi = {
  getAll: (unreadOnly = false) =>
    fetch(`${API_URL}/notifications${unreadOnly ? '?unread_only=true' : ''}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),
    
  markRead: (notificationId) =>
    fetch(`${API_URL}/notifications/${notificationId}/read`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),
    
  markAllRead: () =>
    fetch(`${API_URL}/notifications/read-all`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse)
};

// Audit Logs API
export const auditLogsApi = {
  getAll: (entityType, entityId, userId, limit = 100) => {
    const params = new URLSearchParams();
    if (entityType) params.append('entity_type', entityType);
    if (entityId) params.append('entity_id', entityId);
    if (userId) params.append('user_id', userId);
    params.append('limit', limit);
    return fetch(`${API_URL}/audit-logs?${params}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse);
  }
};

// Dashboard API
export const dashboardApi = {
  getStats: () =>
    fetch(`${API_URL}/dashboard/stats`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),
    
  getRecentExercises: (limit = 5) =>
    fetch(`${API_URL}/dashboard/recent-exercises?limit=${limit}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  getCharts: () =>
    fetch(`${API_URL}/dashboard/charts`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse)
};

// Reports API
export const reportsApi = {
  getExerciseReportJson: (exerciseId) =>
    fetch(`${API_URL}/reports/exercise/${exerciseId}/json`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),
    
  downloadExerciseReportPdf: async (exerciseId) => {
    const response = await fetch(`${API_URL}/reports/exercise/${exerciseId}/pdf`, {
      headers: getHeaders(),
      credentials: 'omit'
    });
    if (!response.ok) throw new Error('Failed to download report');
    return response.blob();
  },

  downloadHierarchicalPdf: async ({ clientId, subCompanyId, exerciseIds }) => {
    const params = new URLSearchParams();
    if (clientId && clientId !== 'all') params.set('client_id', clientId);
    if (subCompanyId && subCompanyId !== 'all') params.set('sub_company_id', subCompanyId);
    if (exerciseIds && exerciseIds.length) params.set('exercise_ids', exerciseIds.join(','));

    const response = await fetch(`${API_URL}/reports/hierarchical/pdf?${params.toString()}`, {
      headers: getHeaders(),
      credentials: 'omit'
    });
    if (!response.ok) throw new Error('Download failed');
    return response.blob();
  },
  
  getHierarchical: ({ clientId, subCompanyId, exerciseId } = {}) => {
    const params = new URLSearchParams();
    if (clientId && clientId !== 'all') params.set('client_id', clientId);
    if (subCompanyId && subCompanyId !== 'all') params.set('sub_company_id', subCompanyId);
    if (exerciseId) params.set('exercise_id', exerciseId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    
    return fetch(`${API_URL}/reports/hierarchical${qs}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse);
  },
  
  getGraphicalData: ({ clientId, subCompanyId, exerciseId } = {}) => {
    const params = new URLSearchParams();
    if (clientId && clientId !== 'all') params.set('client_id', clientId);
    if (subCompanyId && subCompanyId !== 'all') params.set('sub_company_id', subCompanyId);
    if (exerciseId) params.set('exercise_id', exerciseId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    
    return fetch(`${API_URL}/reports/graphical-data${qs}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse);
  },

  emailInjectReport: ({ exerciseId, clientId, subCompanyId, exerciseIds, recipients } = {}) => {
    const body = {};
    if (exerciseId) body.exercise_id = exerciseId;
    if (clientId && clientId !== 'all') body.client_id = clientId;
    if (subCompanyId && subCompanyId !== 'all') body.sub_company_id = subCompanyId;
    if (exerciseIds && exerciseIds.length) body.exercise_ids = exerciseIds.join(',');
    if (recipients && recipients.length) body.recipients = recipients;

    return fetch(`${API_URL}/reports/email-pdf`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(body),
    }).then(handleResponse);
  },
};

export const brandingApi = {
  get: () =>
    fetch(`${API_URL}/branding`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  getPublic: () =>
    fetch(`${API_URL}/branding/public`, {
      credentials: 'omit'
    }).then(handleResponse),

  update: (data) =>
    fetch(`${API_URL}/branding`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse)
};

// Export API
export const exportApi = {
  exercisesCsv: async () => {
    const response = await fetch(`${API_URL}/export/exercises/csv`, {
      headers: getHeaders(),
      credentials: 'omit'
    });
    if (!response.ok) throw new Error('Export failed');
    return response.blob();
  },
  
  exercisesExcel: async ({ clientId, subCompanyId } = {}) => {
    const params = new URLSearchParams();
    if (clientId && clientId !== 'all') params.set('client_id', clientId);
    if (subCompanyId && subCompanyId !== 'all') params.set('sub_company_id', subCompanyId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    
    const response = await fetch(`${API_URL}/export/exercises/excel${qs}`, {
      headers: getHeaders(),
      credentials: 'omit'
    });
    if (!response.ok) throw new Error('Export failed');
    return response.blob();
  },
  
  usersCsv: async () => {
    const response = await fetch(`${API_URL}/export/users/csv`, {
      headers: getHeaders(),
      credentials: 'omit'
    });
    if (!response.ok) throw new Error('Export failed');
    return response.blob();
  },
  
  injectsCsv: async ({ exerciseId, clientId, subCompanyId } = {}) => {
    const params = new URLSearchParams();
    if (exerciseId && exerciseId !== 'all') params.set('exercise_id', exerciseId);
    if (clientId && clientId !== 'all') params.set('client_id', clientId);
    if (subCompanyId && subCompanyId !== 'all') params.set('sub_company_id', subCompanyId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    
    const response = await fetch(`${API_URL}/export/injects/csv${qs}`, {
      headers: getHeaders(),
      credentials: 'omit'
    });
    if (!response.ok) throw new Error('Export failed');
    return response.blob();
  },
  
  injectsExcel: async ({ exerciseId, clientId, subCompanyId } = {}) => {
    const params = new URLSearchParams();
    if (exerciseId && exerciseId !== 'all') params.set('exercise_id', exerciseId);
    if (clientId && clientId !== 'all') params.set('client_id', clientId);
    if (subCompanyId && subCompanyId !== 'all') params.set('sub_company_id', subCompanyId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    
    const response = await fetch(`${API_URL}/export/injects/excel${qs}`, {
      headers: getHeaders(),
      credentials: 'omit'
    });
    if (!response.ok) throw new Error('Export failed');
    return response.blob();
  }
};

// Messages API - Inter-role communication
export const messagesApi = {
  getAll: (exerciseId, unreadOnly = false) => {
    const params = new URLSearchParams();
    if (exerciseId) params.set('exercise_id', exerciseId);
    if (unreadOnly) params.set('unread_only', 'true');
    const qs = params.toString() ? `?${params.toString()}` : '';
    
    return fetch(`${API_URL}/messages${qs}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse);
  },

  get: (messageId) =>
    fetch(`${API_URL}/messages/${messageId}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  send: (data) =>
    fetch(`${API_URL}/messages`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),

  reply: (messageId, content) =>
    fetch(`${API_URL}/messages/${messageId}/reply`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify({ message_id: messageId, content })
    }).then(handleResponse),

  getUnreadCount: (exerciseId) => {
    const params = exerciseId ? `?exercise_id=${exerciseId}` : '';
    return fetch(`${API_URL}/messages/unread/count${params}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse);
  }
};

// Activity Logs API - Structured operational logging
export const activityLogsApi = {
  create: (data) =>
    fetch(`${API_URL}/activity-logs`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),

  getAll: (exerciseId, logType = null, limit = 100) => {
    const params = new URLSearchParams();
    params.set('exercise_id', exerciseId);
    if (logType) params.set('log_type', logType);
    params.set('limit', limit.toString());
    
    return fetch(`${API_URL}/activity-logs?${params.toString()}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse);
  },

  getUnifiedStream: (exerciseId, limit = 50) =>
    fetch(`${API_URL}/activity-logs/unified-stream?exercise_id=${exerciseId}&limit=${limit}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  getRoles: (exerciseId) =>
    fetch(`${API_URL}/activity-logs/roles?exercise_id=${exerciseId}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  delete: (logId) =>
    fetch(`${API_URL}/activity-logs/${logId}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  updateNotes: (logId, notes) =>
    fetch(`${API_URL}/activity-logs/${logId}/notes`, {
      method: 'PATCH',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify({ notes }),
    }).then(handleResponse),

  getOne: (logId) =>
    fetch(`${API_URL}/activity-logs/${logId}`, {
      headers: getHeaders(),
      credentials: 'omit',
    }).then(handleResponse),

  addReply: (logId, message, attachments = []) =>
    fetch(`${API_URL}/activity-logs/${logId}/replies`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify({ message, attachments }),
    }).then(handleResponse),

  exportCsv: (exerciseId) =>
    fetch(`${API_URL}/activity-logs/${exerciseId}/export?format=csv`, {
      headers: getHeaders(),
      credentials: 'omit',
    }).then(res => res.blob()).then(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity_logs_${exerciseId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }),
};

// Scenario Timeline API - CSV-based planned timeline
export const scenarioTimelineApi = {
  upload: async (exerciseId, file) => {
    const token = localStorage.getItem('ems_token');
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${API_URL}/scenario-timeline/upload/${exerciseId}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      credentials: 'omit',
      body: formData
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || 'Upload failed');
    }
    return response.json();
  },

  get: (exerciseId) =>
    fetch(`${API_URL}/scenario-timeline/${exerciseId}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  getCurrentPosition: (exerciseId) =>
    fetch(`${API_URL}/scenario-timeline/${exerciseId}/current-position`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  updateItemStatus: (exerciseId, itemId, status) =>
    fetch(`${API_URL}/scenario-timeline/${exerciseId}/item/${itemId}/status?status=${status}`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  delete: (exerciseId) =>
    fetch(`${API_URL}/scenario-timeline/${exerciseId}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  export: (exerciseId) =>
    fetch(`${API_URL}/scenario-timeline/${exerciseId}/export`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse)
};

// PBX API - Internal phone system
export const pbxApi = {
  // Phone Numbers
  createNumber: (data) =>
    fetch(`${API_URL}/pbx/numbers`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),

  listNumbers: (exerciseId) =>
    fetch(`${API_URL}/pbx/numbers?exercise_id=${exerciseId}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  updateNumber: (numberId, data) =>
    fetch(`${API_URL}/pbx/numbers/${numberId}`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),

  deleteNumber: (numberId) =>
    fetch(`${API_URL}/pbx/numbers/${numberId}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  bulkCreateNumbers: (exerciseId, count = 10) =>
    fetch(`${API_URL}/pbx/numbers/bulk-create/${exerciseId}?count=${count}`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  assignNumber: (numberId, userId) =>
    fetch(`${API_URL}/pbx/assign/${numberId}?user_id=${userId}`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  getMyPhone: (exerciseId) =>
    fetch(`${API_URL}/pbx/my-phone?exercise_id=${exerciseId}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  // Hunt Groups
  createHuntGroup: (data) =>
    fetch(`${API_URL}/pbx/hunt-groups`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),

  listHuntGroups: (exerciseId) =>
    fetch(`${API_URL}/pbx/hunt-groups?exercise_id=${exerciseId}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  deleteHuntGroup: (groupId) =>
    fetch(`${API_URL}/pbx/hunt-groups/${groupId}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  // Call Logs
  logCall: (data) =>
    fetch(`${API_URL}/pbx/calls/log`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),

  listCalls: (exerciseId, limit = 50) =>
    fetch(`${API_URL}/pbx/calls?exercise_id=${exerciseId}&limit=${limit}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  getCallStats: (exerciseId) =>
    fetch(`${API_URL}/pbx/calls/stats?exercise_id=${exerciseId}`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  // Voicemail
  createVoicemail: (data) =>
    fetch(`${API_URL}/pbx/voicemail`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),

  listVoicemails: (exerciseId, extension = null) => {
    let url = `${API_URL}/pbx/voicemail?exercise_id=${exerciseId}`;
    if (extension) url += `&extension=${extension}`;
    return fetch(url, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse);
  },

  markVoicemailRead: (voicemailId) =>
    fetch(`${API_URL}/pbx/voicemail/${voicemailId}/read`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse)
};


// SIP Config API - FreeSWITCH / External Calls
export const sipConfigApi = {
  getConfig: () =>
    fetch(`${API_URL}/sip-config`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  updateConfig: (data) =>
    fetch(`${API_URL}/sip-config`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),

  testConnection: () =>
    fetch(`${API_URL}/sip-config/test`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  logExternalCall: (data) =>
    fetch(`${API_URL}/sip-config/external-call-log`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),

  getExternalCallLogs: (exerciseId, limit = 50) => {
    let url = `${API_URL}/sip-config/external-call-logs?limit=${limit}`;
    if (exerciseId) url += `&exercise_id=${exerciseId}`;
    return fetch(url, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse);
  }
};


// Telnyx Phone Number Management API (admin)
export const telnyxNumbersApi = {
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return fetch(`${API_URL}/telnyx/numbers${qs ? '?' + qs : ''}`, {
      headers: getHeaders(),
      credentials: 'omit',
    }).then(handleResponse);
  },

  create: (data) =>
    fetch(`${API_URL}/telnyx/numbers`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data),
    }).then(handleResponse),

  search: (data) =>
    fetch(`${API_URL}/telnyx/numbers/search`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data),
    }).then(handleResponse),

  purchase: (data) =>
    fetch(`${API_URL}/telnyx/numbers/purchase`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data),
    }).then(handleResponse),

  update: (id, data) =>
    fetch(`${API_URL}/telnyx/numbers/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data),
    }).then(handleResponse),

  delete: (id, releaseTelnyx = false) =>
    fetch(`${API_URL}/telnyx/numbers/${id}?release_telnyx=${releaseTelnyx}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'omit',
    }).then(handleResponse),
};

// Telnyx Exercise Telephony Config API
export const telnyxConfigApi = {
  listForExercise: (exerciseId) =>
    fetch(`${API_URL}/telnyx/exercise-config/by-exercise/${exerciseId}`, {
      headers: getHeaders(),
      credentials: 'omit',
    }).then(handleResponse),

  create: (data) =>
    fetch(`${API_URL}/telnyx/exercise-config`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data),
    }).then(handleResponse),

  update: (configId, data) =>
    fetch(`${API_URL}/telnyx/exercise-config/${configId}`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data),
    }).then(handleResponse),

  remove: (configId) =>
    fetch(`${API_URL}/telnyx/exercise-config/${configId}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'omit',
    }).then(handleResponse),

  outboundCall: (data) =>
    fetch(`${API_URL}/telnyx/outbound-call`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data),
    }).then(handleResponse),

  callLogs: (exerciseId, limit = 100) => {
    let url = `${API_URL}/telnyx/call-logs?limit=${limit}`;
    if (exerciseId) url += `&exercise_id=${exerciseId}`;
    return fetch(url, {
      headers: getHeaders(),
      credentials: 'omit',
    }).then(handleResponse);
  },
};

// Workshop Pre-flight Diagnostic API
export const preflightApi = {
  getState: (exerciseId) =>
    fetch(`${API_URL}/preflight/${exerciseId}/state`, {
      headers: getHeaders(),
      credentials: 'omit',
    }).then(handleResponse),

  testCall: (exerciseId, fromExt, toExt) =>
    fetch(`${API_URL}/preflight/${exerciseId}/test-call`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify({ from_extension: fromExt, to_extension: toExt }),
    }).then(handleResponse),

  numberHealth: (exerciseId) =>
    fetch(`${API_URL}/workshop/${exerciseId}/number-health`, {
      headers: getHeaders(),
      credentials: 'omit',
    }).then(handleResponse),
};

// Live Workshop Dashboard API
export const workshopApi = {
  getLive: (exerciseId) =>
    fetch(`${API_URL}/workshop/${exerciseId}/live`, {
      headers: getHeaders(),
      credentials: 'omit',
    }).then(handleResponse),

  hangupAll: (exerciseId) =>
    fetch(`${API_URL}/workshop/${exerciseId}/hangup-all`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
    }).then(handleResponse),

  muteAll: (exerciseId) =>
    fetch(`${API_URL}/workshop/${exerciseId}/mute-all`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
    }).then(handleResponse),
};



// Exercise Custom Tabs API
export const exerciseTabsApi = {
  getTabs: (exerciseId) =>
    fetch(`${API_URL}/exercises/${exerciseId}/tabs`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  createTab: (exerciseId, name) =>
    fetch(`${API_URL}/exercises/${exerciseId}/tabs`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify({ name })
    }).then(handleResponse),

  updateTab: (exerciseId, tabId, data) =>
    fetch(`${API_URL}/exercises/${exerciseId}/tabs/${tabId}`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),

  deleteTab: (exerciseId, tabId) =>
    fetch(`${API_URL}/exercises/${exerciseId}/tabs/${tabId}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  uploadFile: (exerciseId, tabId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const headers = getHeaders();
    delete headers['Content-Type']; // let browser set multipart boundary
    return fetch(`${API_URL}/exercises/${exerciseId}/tabs/${tabId}/files`, {
      method: 'POST',
      headers,
      credentials: 'omit',
      body: formData
    }).then(handleResponse);
  },

  deleteFile: (exerciseId, tabId, fileId) =>
    fetch(`${API_URL}/exercises/${exerciseId}/tabs/${tabId}/files/${fileId}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  togglePin: (exerciseId, tabId, fileId, pinned) =>
    fetch(`${API_URL}/exercises/${exerciseId}/tabs/${tabId}/files/${fileId}/pin`, {
      method: 'PATCH',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify({ pinned })
    }).then(handleResponse),
};

// Exercise Map API
export const exerciseMapApi = {
  getMap: (exerciseId) =>
    fetch(`${API_URL}/exercises/${exerciseId}/map`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  updateMap: (exerciseId, data) =>
    fetch(`${API_URL}/exercises/${exerciseId}/map`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),

  getMapVersion: (exerciseId) =>
    fetch(`${API_URL}/exercises/${exerciseId}/map/version`, {
      headers: getHeaders(),
      credentials: 'omit'
    }).then(handleResponse),

  // V14.5: per-feature endpoints to avoid multi-user save collisions
  addFeature: (exerciseId, feature) =>
    fetch(`${API_URL}/exercises/${exerciseId}/map/features`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
      body: JSON.stringify({ feature }),
    }).then(handleResponse),

  deleteFeature: (exerciseId, featureId) =>
    fetch(`${API_URL}/exercises/${exerciseId}/map/features/${encodeURIComponent(featureId)}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'omit',
    }).then(handleResponse),

  // V14.10.3 — admin clears the "Last edit" indicator banner on the map
  clearChangeIndicator: (exerciseId) =>
    fetch(`${API_URL}/exercises/${exerciseId}/map/clear-change-indicator`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'omit',
    }).then(handleResponse),
};


// Exercise Phone Contacts (Roleplay Directory) API
export const phoneContactsApi = {
  list: (exerciseId) =>
    fetch(`${API_URL}/exercises/${exerciseId}/phone-contacts`, {
      headers: getHeaders(), credentials: 'omit'
    }).then(handleResponse),

  create: (exerciseId, data) =>
    fetch(`${API_URL}/exercises/${exerciseId}/phone-contacts`, {
      method: 'POST', headers: getHeaders(), credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),

  update: (exerciseId, contactId, data) =>
    fetch(`${API_URL}/exercises/${exerciseId}/phone-contacts/${contactId}`, {
      method: 'PUT', headers: getHeaders(), credentials: 'omit',
      body: JSON.stringify(data)
    }).then(handleResponse),

  updateStatus: (exerciseId, contactId, status) =>
    fetch(`${API_URL}/exercises/${exerciseId}/phone-contacts/${contactId}/status`, {
      method: 'PATCH', headers: getHeaders(), credentials: 'omit',
      body: JSON.stringify({ status })
    }).then(handleResponse),

  delete: (exerciseId, contactId) =>
    fetch(`${API_URL}/exercises/${exerciseId}/phone-contacts/${contactId}`, {
      method: 'DELETE', headers: getHeaders(), credentials: 'omit'
    }).then(handleResponse),
};

// ==================== MEDIA PORTAL BRIDGE API ====================

export const mediaPortalApi = {
  // Bridge endpoints
  getBridgeStatus: (exerciseId) =>
    fetch(`${API_URL}/media-portal/bridge-status/${exerciseId}`, {
      headers: getHeaders(), credentials: 'omit'
    }).then(handleResponse),

  getSettings: (exerciseId) =>
    fetch(`${API_URL}/media-portal/settings/${exerciseId}`, {
      headers: getHeaders(), credentials: 'omit'
    }).then(handleResponse),

  // V14.10.9 — reverse lookup by MP exercise id
  getSettingsByMp: (mpExerciseId) =>
    fetch(`${API_URL}/media-portal/settings/by-mp/${mpExerciseId}`, {
      headers: getHeaders(), credentials: 'omit'
    }).then(handleResponse),

  updateSettings: (exerciseId, settings) =>
    fetch(`${API_URL}/media-portal/settings/${exerciseId}`, {
      method: 'PUT', headers: getHeaders(), credentials: 'omit',
      body: JSON.stringify(settings)
    }).then(handleResponse),

  linkExercise: (exerciseId, mpExerciseId = null) => {
    const params = mpExerciseId ? `?mp_exercise_id=${mpExerciseId}` : '';
    return fetch(`${API_URL}/media-portal/link-exercise/${exerciseId}${params}`, {
      method: 'POST', headers: getHeaders(), credentials: 'omit'
    }).then(handleResponse);
  },

  publishInject: (injectId, config = {}) =>
    fetch(`${API_URL}/media-portal/publish-inject/${injectId}`, {
      method: 'POST', headers: getHeaders(), credentials: 'omit',
      body: JSON.stringify(config)
    }).then(handleResponse),

  publishPost: (postId) =>
    fetch(`${API_URL}/media-portal/publish-post/${postId}`, {
      method: 'POST', headers: getHeaders(), credentials: 'omit'
    }).then(handleResponse),

  createPost: (exerciseId, postData) =>
    fetch(`${API_URL}/media-portal/create-post/${exerciseId}`, {
      method: 'POST', headers: getHeaders(), credentials: 'omit',
      body: JSON.stringify(postData)
    }).then(handleResponse),

  getAnalytics: (exerciseId) =>
    fetch(`${API_URL}/media-portal/analytics/${exerciseId}`, {
      headers: getHeaders(), credentials: 'omit'
    }).then(handleResponse),

  getPostQueue: (exerciseId) =>
    fetch(`${API_URL}/media-portal/post-queue/${exerciseId}`, {
      headers: getHeaders(), credentials: 'omit'
    }).then(handleResponse),

  listExercises: () =>
    fetch(`${API_URL}/media-portal/exercises`, {
      headers: getHeaders(), credentials: 'omit'
    }).then(handleResponse),

  listPlatforms: () =>
    fetch(`${API_URL}/media-portal/platforms`, {
      headers: getHeaders(), credentials: 'omit'
    }).then(handleResponse),

  listPosts: (exerciseId) =>
    fetch(`${API_URL}/media-portal/posts/${exerciseId}`, {
      headers: getHeaders(), credentials: 'omit'
    }).then(handleResponse),

  getHubStats: () =>
    fetch(`${API_URL}/media-portal/hub-stats`, {
      headers: getHeaders(), credentials: 'omit'
    }).then(handleResponse),
};
